import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import _ from 'lodash';
import { WithStyles, withStyles } from '@mui/styles';
import classnames from 'classnames';
import { bindActionCreators, Dispatch } from 'redux';

// Contexts
import { withTranslate } from '../../core/TenantProvider';
// Components, Atoms, Molecules, Organisms
import APSuccessMessage from '../../components/atoms/APSuccessMessage';
import APHeading from '../../components/atoms/APHeading';
import APInput from '../../components/atoms/APInput';
import APButton from '../../components/atoms/APButton';
import { APSelectComboFormik } from '../../components/atoms/APSelect';
import APPasswordReset from '../../components/organisms/APPasswordReset';
import APAddressDetails from '../../components/organisms/APAddressDetails/APAddressDetails';
import APEmailPreferenceSection from '../../components/organisms/APEmailPreferenceSection';
import APClientDetailsSection from '../../components/organisms/APClientDetailsSection/APClientDetailsSection';
import APBackdropLoader from '../../components/atoms/APBackdropLoader';
import APErrorMessage from '../../components/atoms/APErrorMessage';
import APBankDetails from '../../components/organisms/APBankDetails/APBankDetails';
import AddBankDetailsContainer from '../AddBankDetailsContainer';
// Files in the same folder
import SettingsContainerStyles from './styles/SettingsContainer.style';
import { ValidationSchema } from './ValidationSchema';
// Redux
import { ReducerState } from '../../redux/modules';
import { actions as UserActions } from '../../redux/modules/user';
import { action as BankDetailsActions } from '../../redux/modules/bankDetails';
// Types
import { CountriesAllDataAttributes } from '../../core/types/CountriesList';
// Utils
import {
    getClientType,
    getSelectedClientLanguages,
    getSelectedCountries,
    getSelectedEmailPreferences,
    getSelectedIndustries,
    dialingCodeMapping
} from './utils/getters';
import joinArray from './utils/joinArray';
import { GetCountriesData, GetCountriesDataWithDefaultLanguage } from '../../core/utils/GetCountriesData';
import { CountryListType } from '../../core/utils/GetCountriesData/GetCountriesData';
import EncodeData from '../../core/utils/EncodeDecodeData';
// Constants
import {
    CLIENT_COUNTRY_CODE,
    EMAIL_PREFERENCES_MAPPING,
    UPDATE_DETAILS_ERROR_CODES
} from '../../core/utils/Constants/Constants';

type ReferenceData = {
    label: string;
    value: string;
    icon: string;
};

interface SettingsContainerProps extends WithStyles<typeof SettingsContainerStyles> {
    translate: Function;
    affiliateDetails: any;
    updateAffiliateDetails: Function;
    updateSettingsStatus: string | null;
    loadingState: string;
    updateSettingsStatusToInitial: Function;
    updateDetailsErrorCode: string;
    createLoadingState: string;
    deleteBankDetailsLoadingState: string;
    resetDeleteBankDetails: Function;
    resetCreateBankDetails: Function;
    getBankDetailsV2: Function;
    isSuperUser: boolean;
}

export interface InitialSettingValues {
    selectedClientLanguages: Array<string>,
    selectedCountries: Array<string>
    selectedIndustries: Array<string>,
    selectedEmailPreferences: Array<string>,
    clientTypes: any,
    companyDetails: {
        tradingName: string,
        companyName: string,
    },
    personalDetails: {
        firstName: string,
        lastName: string
    },
    contactDetails: {
        emailAddress: string,
        countryISDCode: string,
        mobileNumber: string
    },
    address: {
        addressLineNo1: string,
        city: string,
        state: string,
        postCode: string,
        country: string
    },
    optOutOfMarketing: boolean,
    getAccountManagerEmails: boolean,
    getNotificationEmails: boolean,
    displayName: string,
    currentPassword: string,
    isEditPassword: boolean,
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
    isSamePassword: boolean,
    addressCountryName: string,
    image: string
}

const SettingsContainer: React.FC<SettingsContainerProps> = ({
    classes,
    translate,
    affiliateDetails,
    updateAffiliateDetails,
    updateSettingsStatus,
    loadingState,
    updateSettingsStatusToInitial,
    updateDetailsErrorCode,
    createLoadingState,
    deleteBankDetailsLoadingState,
    resetDeleteBankDetails,
    resetCreateBankDetails,
    getBankDetailsV2,
    isSuperUser
}) => {
    const formRef = useRef<any>(null);
    const [editPassword, setEditPassword] = useState<boolean>(false);
    const [editAddress, setEditAddress] = useState<boolean>(false);
    const [isEditLinkClicked, setIsEditLinkClicked] = useState<boolean>(false);
    const [isMobileView, setIsMobileView] = useState<boolean>(false);
    const [isAddBankDetailsView, setIsAddBankDetailsView] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);

    const userDetails = affiliateDetails?.affiliate || {};
    const clientDetails = affiliateDetails?.ClientData || {};

    const {
        client_Type: clientType,
        trading_name: tradingName,
        company_name: companyName,
        user_first_name: firstName,
        user_last_name: lastName,
        user_emailid: emailAddress,
        user_country_isdcode: countryISDCode,
        user_phone_no: mobileNumber,
        first_line: addressLineNo1,
        town: city,
        county_state: state,
        post_code: postCode,
        country,
        opt_me_out_of_marketing: optOutOfMarketing,
        notification_required: getNotificationEmails,
        pwd: oldPassword
    } = userDetails;

    const initialValues: InitialSettingValues = {
        selectedClientLanguages: getSelectedClientLanguages(clientDetails),
        selectedCountries: getSelectedCountries(clientDetails),
        selectedIndustries: getSelectedIndustries(clientDetails),
        selectedEmailPreferences: getSelectedEmailPreferences(userDetails),
        clientTypes: getClientType(clientType),
        companyDetails: {
            tradingName: tradingName || '',
            companyName: tradingName ? companyName || '' : '',
        },
        personalDetails: {
            firstName: firstName || '',
            lastName: lastName || ''
        },
        contactDetails: {
            emailAddress: emailAddress || '',
            countryISDCode: countryISDCode ? `+${countryISDCode}` : '',
            mobileNumber: mobileNumber || ''
        },
        address: {
            addressLineNo1: addressLineNo1 || '',
            city: city || '',
            state: state || '',
            postCode: postCode || '',
            country: country || ''
        },
        optOutOfMarketing: optOutOfMarketing,
        getAccountManagerEmails: clientDetails?.account_Manager_Emails,
        getNotificationEmails: !!getNotificationEmails,
        displayName: '',

        currentPassword: 'XXXXXXXX',
        isEditPassword: false,
        oldPassword: `${oldPassword || ''}`,
        newPassword: '',
        confirmPassword: '',
        isSamePassword: false,
        addressCountryName: '',
        image: ''
    };

    const updateAddressCountryDetails = async (addressCountryCode: string) => {
        const countries: CountriesAllDataAttributes = GetCountriesDataWithDefaultLanguage();
        const languageWiseCountriesData: CountryListType = GetCountriesData('setting');

        let addressCountryObj: any = countries.options.find(
            (option: ReferenceData) => option.label === addressCountryCode || option.value === addressCountryCode
        );

        if (addressCountryObj) {
            const countryCode: string = addressCountryObj.value;
            addressCountryObj = languageWiseCountriesData.options.find(
                (option: ReferenceData) => option.label === countryCode || option.value === countryCode
            ) || addressCountryObj;
        }

        if (!addressCountryObj) return;

        const { label, icon } = addressCountryObj;

        if (icon && label) {
            try {
                const { default: image } = await import(`../../core/assets/CountriesList/${icon}.svg`);
                formRef.current.setValues((prevState) => ({
                    ...prevState,
                    addressCountryName: label,
                    image
                }));
            } catch (error) {
                console.error("Error loading country icon:", error);
            }
        }
    }

    const onSubmit = (values: any) => {
        if (editAddress || editPassword) {
            setEditAddress(false);
            setEditPassword(false);
        }

        const {
            contactDetails,
            address,
            clientTypes,
            optOutOfMarketing,
            getAccountManagerEmails,
            selectedClientLanguages,
            selectedCountries,
            selectedEmailPreferences,
            selectedIndustries,
            getNotificationEmails,
            currentPassword,
            newPassword
        } = values;

        // Client countries mapping
        const countryName = Object.fromEntries(Object.entries(CLIENT_COUNTRY_CODE).map(a => a.reverse()));
        let updatedClientCountries = selectedCountries.map((clientCountry: string) => {
            return countryName[clientCountry];
        });

        // Client Industries mapping
        let updatedClientLanguages = selectedClientLanguages.map((clientLanguage: string) => {
            return clientLanguage.charAt(0).toUpperCase() + clientLanguage.substr(1).toLowerCase()
        });

        // Client Industries mapping
        let updatedClientIndustries = selectedIndustries.map((clientIndustry: string) => {
            return clientIndustry.charAt(0).toUpperCase() + clientIndustry.substr(1).toLowerCase()
        });

        // Client type mapping
        let updatedClientTypes: Array<string> = [];
        if (clientTypes.personal) {
            updatedClientTypes.push('Personal Clients');
        }
        if (clientTypes.onlineMarketplaceSellers) {
            updatedClientTypes.push('Online marketplace sellers');
        }
        if (clientTypes.corporate) {
            updatedClientTypes.push('Corporate clients');
        }

        // Email preference mapping
        const allEmailPreferences = {
            weekly_market_updates: false,
            promotions: false,
            product_news: false,
            newsletters: false,
            news_flash: false,
            daily_update: false,
            research_development: false
        };

        selectedEmailPreferences.forEach((clientEmailPreference: string) => {
            clientEmailPreference && EMAIL_PREFERENCES_MAPPING[clientEmailPreference] && (allEmailPreferences[EMAIL_PREFERENCES_MAPPING[clientEmailPreference]] = true);
        });

        const addressCountryCode = address.country;
        updateAddressCountryDetails(addressCountryCode);

        const newEncryptedCurrentPassword: string = currentPassword !== '' ? EncodeData(currentPassword) : '';
        const newEncryptedPassword: string = newPassword !== '' ? EncodeData(newPassword) : '';

        updateAffiliateDetails(
            contactDetails,
            address,
            allEmailPreferences,
            joinArray(updatedClientTypes).toString(),
            joinArray(updatedClientIndustries).toString(),
            joinArray(updatedClientLanguages).toString(),
            joinArray(updatedClientCountries).toString(),
            getAccountManagerEmails,
            optOutOfMarketing,
            getNotificationEmails,
            newEncryptedCurrentPassword,
            newEncryptedPassword
        );

        window.scrollTo(0, 0);
    };

    const handleResize = () => {
        const changeSubmitButtonPosition: boolean = window.innerWidth <= 940;
        if (isMobileView !== changeSubmitButtonPosition) {
            setIsMobileView(changeSubmitButtonPosition)
        }
    }

    const onMessageClose = () => {
        updateSettingsStatusToInitial();
    };

    const addBankDetailsSectionShowHide = ({ view }) => {
        setIsAddBankDetailsView(view);
    }

    useEffect(() => {
        updateSettingsStatusToInitial();

        if (affiliateDetails?.affiliate?.country) {
            const addressCountryCode = affiliateDetails.affiliate.country;
            updateAddressCountryDetails(addressCountryCode);
        }

        const { address: { addressLineNo1 = '', city = '', state = '', country = '', postCode = '' } = {} } =
            formRef.current?.errors || {};
        const shouldEditAddress = isEditLinkClicked || addressLineNo1 || city || state || country || postCode;

        shouldEditAddress && setEditAddress(shouldEditAddress);

        handleResize();

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (!_.isEqual(affiliateDetails?.affiliate?.country, userDetails?.country)) {
            updateAddressCountryDetails(userDetails.country);
        }
    }, [affiliateDetails?.affiliate?.country, userDetails?.country]);

    useEffect(() => {
        if (formRef.current) {
            const { address: { addressLineNo1 = '', city = '', state = '', country = '', postCode = '' } = {} } =
                formRef.current.errors || {};
            const shouldEditAddress = isEditLinkClicked || addressLineNo1 || city || state || country || postCode;

            setEditAddress(shouldEditAddress);
        }
    }, [isEditLinkClicked, formRef.current?.errors]);

    useEffect(() => {
        if (updateDetailsErrorCode !== '') {
            const { INCORRECT_CURRENT_PASSWORD, INCORRECT_NEW_PASSWORD } = UPDATE_DETAILS_ERROR_CODES;

            switch (updateDetailsErrorCode) {
                case INCORRECT_CURRENT_PASSWORD:
                    formRef.current?.setValues((curValues: any) => ({
                        ...curValues,
                        currentPassword: '',
                        isEditPassword: true
                    }));
                    setEditPassword(true);
                    break;
                case INCORRECT_NEW_PASSWORD:
                    formRef.current?.setValues((curValues: any) => ({
                        ...curValues,
                        newPassword: '',
                        confirmPassword: '',
                        isEditPassword: true
                    }));
                    setEditPassword(true);
                    break;
                default:
                    break;
            }
        }
    }, [updateDetailsErrorCode]);

    useEffect(() => {
        const resetMessageStates = () => {
            setShowSuccessMessage(false);
            setShowErrorMessage(false);
            setAlertMessage(null);
        };

        if (isAddBankDetailsView) {
            resetMessageStates();
        }
    }, [isAddBankDetailsView]);

    const handleMessageStates = (isSuccess: boolean, message: string) => {
        setShowSuccessMessage(isSuccess);
        setShowErrorMessage(!isSuccess);
        setAlertMessage(message);
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        const setErrorMessage = (updateDetailsErrorCode: string) => {
            const { INCORRECT_CURRENT_PASSWORD, INCORRECT_NEW_PASSWORD } = UPDATE_DETAILS_ERROR_CODES;

            switch (updateDetailsErrorCode) {
                case INCORRECT_NEW_PASSWORD:
                    return translate('settings.password.newpassword.match.oldpassword.error');
                case INCORRECT_CURRENT_PASSWORD:
                    return translate('settings.password.oldpassword.label.error');
                default:
                    return translate('settings.updatedstate.error.message');
            }
        };

        if (updateSettingsStatus === 'success' && formRef.current) {
            formRef.current.setValues((prevState: any) => ({
                ...prevState,
                currentPassword: 'XXXXXXXX',
                newPassword: '',
                confirmPassword: '',
                isSamePassword: false
            }));

            formRef.current.setFieldTouched('newPassword', false);
            formRef.current.setFieldTouched('currentPassword', false);
            formRef.current.setFieldTouched('confirmPassword', false);

            handleMessageStates(true, translate('settings.updatedstate.success.message'));
        }

        if (updateSettingsStatus === 'error') {
            handleMessageStates(false, setErrorMessage(updateDetailsErrorCode));
        }

        if (createLoadingState === 'SUCCESS') {
            handleMessageStates(true, translate('settings.create.bank.success.message'));
            resetCreateBankDetails();
        }

        if (createLoadingState === 'ERROR') {
            handleMessageStates(false, translate('settings.create.bank.error.message'));
            resetCreateBankDetails();
        }

        if (deleteBankDetailsLoadingState === 'SUCCESS') {
            handleMessageStates(true, translate('settings.delete.bank.success.message'));
            getBankDetailsV2(affiliateDetails.affiliate.affiliate_Number);
            resetDeleteBankDetails();
        }

        if (deleteBankDetailsLoadingState === 'ERROR') {
            handleMessageStates(false, translate('settings.delete.bank.error.message'));
            resetDeleteBankDetails();
        }
    }, [updateSettingsStatus, createLoadingState, deleteBankDetailsLoadingState, translate, updateDetailsErrorCode, resetCreateBankDetails, getBankDetailsV2, affiliateDetails?.affiliate?.affiliate_Number, resetDeleteBankDetails]);

    useEffect(() => {
        if (showSuccessMessage || showErrorMessage) {
            setTimeout(() => {
                setShowSuccessMessage(false);
                setShowErrorMessage(false);
                setAlertMessage(null);
            }, 10000);
        }
    }, [showSuccessMessage, showErrorMessage]);

    return (
        <div className={classes.settingsContainer}>
            <div className={classes.settingsSection}>
                {!isAddBankDetailsView &&
                    <>
                        <APHeading className={classes.settingsSectionHeader} variant="h4">
                            {translate('settings.heading')}
                        </APHeading>
                    </>
                }
                {showSuccessMessage && (
                    <APSuccessMessage successMessage={alertMessage} />
                )}
                {showErrorMessage && (
                    <APErrorMessage errorMessage={alertMessage} onClose={onMessageClose} />
                )}

                {(loadingState === 'UPDATING' || deleteBankDetailsLoadingState === 'LOADING') ?
                    <APBackdropLoader open={true} /> : ''
                }

                {isAddBankDetailsView &&
                    <AddBankDetailsContainer addBankDetailsSectionClose={() => addBankDetailsSectionShowHide({ view: false })} />
                }

                <Formik
                    initialValues={initialValues}
                    validationSchema={ValidationSchema(translate)}
                    onSubmit={onSubmit}
                    innerRef={formRef}
                    enableReinitialize
                >
                    {({ handleChange,
                        values,
                        touched,
                        errors,
                        isValid,
                        handleBlur,
                        setFieldValue
                    }) => (
                        !isAddBankDetailsView &&
                        (<Form>
                            <fieldset
                                disabled={isSuperUser}
                                style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}
                            >
                                <div className={classnames(classes.settingsSectionContainer, classes.settingsSectionContainerMain)}>
                                    <div className={classnames(classes.settingsSectionContainer, classes.settingsSectionContainerFirst)}>
                                        <div className={classes.settingsSectionContentCls}>
                                            <div className={classes.settingsForm}>
                                                <div className={classes.settingsFormSection}>

                                                    <APHeading variant="body1" className={classes.formSectionCopy}>{translate('settings.company.details.copy')}</APHeading>

                                                    <div className={classes.formSectionHeader}>
                                                        <APHeading className={classes.formSectionHeading}
                                                            variant="body1">
                                                            {translate('settings.company.details.heading')}
                                                        </APHeading>
                                                    </div>
                                                    <div className={classes.formSectionContent}>
                                                        <APInput label={translate('settings.company.tradingname.label')}
                                                            value={values.companyDetails.tradingName}
                                                            name="companyDetails.tradingName"
                                                            disabled={true}
                                                            className={classes.formSectionInput}
                                                            isCoralogixMasked={true} />
                                                        <APInput label={translate('settings.company.companyname.label')}
                                                            value={values.companyDetails.companyName}
                                                            name="companyDetails.companyName"
                                                            disabled={true}
                                                            className={classes.formSectionInput}
                                                            isCoralogixMasked={true} />
                                                    </div>
                                                </div>
                                                <div className={classes.settingsFormSection}>
                                                    <div className={classes.formSectionHeader}>
                                                        <APHeading className={classes.formSectionHeading}
                                                            variant="body1">
                                                            {translate('settings.personal.details.heading')}
                                                        </APHeading>
                                                    </div>
                                                    <div className={classes.formSectionContent}>
                                                        <APInput label={translate('settings.personal.firstname.label')}
                                                            value={values.personalDetails.firstName}
                                                            name="personalDetails.firstName"
                                                            disabled={true}
                                                            className={classes.formSectionInput}
                                                            isCoralogixMasked={true} />
                                                        <APInput label={translate('settings.personal.surname.label')}
                                                            value={values.personalDetails.lastName}
                                                            name="personalDetails.lastName"
                                                            disabled={true}
                                                            className={classes.formSectionInput}
                                                            isCoralogixMasked={true} />
                                                    </div>
                                                </div>
                                                <div className={classes.settingsFormSection}>
                                                    <div className={classes.formSectionHeader}>
                                                        <APHeading className={classes.formSectionHeading}
                                                            variant="body1">
                                                            {translate('settings.contact.details.heading')}
                                                        </APHeading>
                                                    </div>
                                                    <div className={classes.formSectionContent}>
                                                        <APInput label={translate('settings.contact.email.label')}
                                                            value={values.contactDetails.emailAddress}
                                                            name="contactDetails.emailAddress"
                                                            disabled={true}
                                                            className={classes.formSectionInput}
                                                            isCoralogixMasked={true}
                                                        />
                                                        <APSelectComboFormik
                                                            key={'mobileNumber'}
                                                            className={classes.formSectionInput}
                                                            error={Boolean(errors.contactDetails?.mobileNumber) || Boolean(errors.contactDetails?.countryISDCode)}
                                                            helperText={(errors.contactDetails?.mobileNumber ? String(errors.contactDetails?.mobileNumber) : errors.contactDetails?.countryISDCode ? String(errors.contactDetails?.countryISDCode) : '')}
                                                            label={translate('settings.contact.mobilenumber.label')}
                                                            countryCodeLabel={translate('settings.contact.mobilecountrycode.label')}
                                                            onChange={handleChange}
                                                            name={'mobileNumber'}
                                                            selectComboFormikNames={['contactDetails.countryISDCode', 'contactDetails.mobileNumber']}
                                                            value={{
                                                                select: values.contactDetails.countryISDCode ? `${values.contactDetails.countryISDCode}` : '',
                                                                input: values.contactDetails.mobileNumber,
                                                                displayName: dialingCodeMapping(`${values.contactDetails.countryISDCode}`)
                                                            }}
                                                            type={'dialing'}
                                                            isCoralogixMasked={true}
                                                        />
                                                    </div>
                                                </div>
                                                <div className={classes.settingsFormSection}>
                                                    <APPasswordReset
                                                        className={classes.settingsFormSection}
                                                        values={values}
                                                        formRef={formRef}
                                                        handleChange={handleChange}
                                                        handleBlur={handleBlur}
                                                        touched={touched}
                                                        errors={errors}
                                                        editPassword={editPassword}
                                                        onEditPassword={(value) => {
                                                            setEditPassword(value)
                                                        }}
                                                    />
                                                </div>
                                                <div className={classes.settingsFormSection}>
                                                    <APAddressDetails
                                                        className={classes.settingsFormSection}
                                                        values={values}
                                                        formRef={formRef}
                                                        handleChange={handleChange}
                                                        errors={errors}
                                                        editAddress={editAddress}
                                                        onEditAddress={(value) => {
                                                            setEditAddress(value)
                                                        }}
                                                        setFieldValue={setFieldValue}
                                                    />
                                                </div>
                                                <div className={classes.settingsFormSection}>
                                                    {!affiliateDetails?.affiliate?.pay_to_group_affiliate &&
                                                        <APBankDetails
                                                            addBankDetailsSectionOpen={() => addBankDetailsSectionShowHide({ view: true })}
                                                            isSuperUser={isSuperUser}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        {!isMobileView && !isSuperUser &&
                                            <div className={classes.settingsButtons}>
                                                <APButton
                                                    className={classes.settingsButton}
                                                    color="primary"
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={!isValid}
                                                >
                                                    {translate('settings.save.button.label')}
                                                </APButton>
                                            </div>
                                        }
                                    </div>
                                    <div className={classnames(classes.settingsSectionContainer, classes.settingsSectionContainerSecond)}>
                                        <div className={classes.settingsSectionContentCls}>
                                            <div className={classes.settingsForm}>
                                                <div className={classes.settingsFormSection}>
                                                    <APClientDetailsSection
                                                        className={classes.settingsFormSection}
                                                        values={values}
                                                        handleChange={handleChange}
                                                        isSuperUser={isSuperUser}
                                                    />
                                                </div>

                                                <div className={classes.settingsFormSection}>
                                                    <APEmailPreferenceSection
                                                        className={classes.settingsFormSection}
                                                        values={values}
                                                        handleChange={handleChange}
                                                        isSuperUser={isSuperUser}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {isMobileView && !isSuperUser &&
                                    <div className={classes.settingsButtons}>
                                        <APButton
                                            className={classes.settingsButton}
                                            color="primary"
                                            type="submit"
                                            variant="contained"
                                            disabled={!isValid}
                                        >
                                            {translate('settings.save.button.label')}
                                        </APButton>
                                    </div>
                                }
                            </fieldset>
                        </Form>)
                    )}
                </Formik>
            </div>
        </div >
    );
}

const mapStateToProps = (store: ReducerState) => ({
    affiliateDetails: store.user.affiliateDetails,
    updateSettingsStatus: store.user.updateSettingsStatus,
    loadingState: store.user.loadingState,
    updateDetailsErrorCode: store.user.updateDetailsErrorCode,
    createLoadingState: store.bankDetails.createLoadingState,
    createBankDetails: store.bankDetails.createBankDetails,
    deleteBankDetailsLoadingState: store.bankDetails.deleteBankDetailsLoadingState,
    isSuperUser: store.user.isSuperUser
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    updateAffiliateDetails: UserActions.updateAffiliateDetails,
    updateSettingsStatusToInitial: UserActions.updateSettingsStatus,
    resetDeleteBankDetails: BankDetailsActions.resetDeleteBankDetails,
    resetCreateBankDetails: BankDetailsActions.resetCreateBankDetails,
    getBankDetailsV2: BankDetailsActions.getBankDetailsV2
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withTranslate(
        withStyles(SettingsContainerStyles)(SettingsContainer))
);